import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Link, Stack, Alert, Typography, Box } from '@mui/material';
import moment from 'moment';
// components
import { FormProvider, RHFTextField } from '../../../components/hook-form';
import useCountdown from '../../../hooks/useCountdown';
import { GoldBorderLoadingButton } from '../../../components/nav-section/vertical/style';
import useLocales from '../../../hooks/useLocales';
import { requestOtp } from '../../../utils/otp';
import { getRegisterErrorMessage } from './registerErrors';

// ----------------------------------------------------------------------

export default function RegisterOTPForm({ setVerification, verification }) {
  const { translate } = useLocales();

  const [resendExpiry, setResendExpiry] = useState(moment().add(1, 'minute').toISOString());

  const countdown = useCountdown(resendExpiry);

  const [isResending, setIsResending] = useState(false);

  const OTPSchema = Yup.object().shape({
    otp: Yup.string().required(translate('x_is_required', { x: 'OTP' })),
  });

  const methods = useForm({
    resolver: yupResolver(OTPSchema),
    defaultValues: { otp: '' },
  });

  const {
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  // Surface an OTP rejection that came back from the final submit.
  useEffect(() => {
    if (verification?.otpError) {
      setError('afterSubmit', { message: verification.otpError });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verification?.otpError]);

  useEffect(() => {
    if (resendExpiry && moment(resendExpiry).valueOf() < moment().valueOf()) {
      setResendExpiry(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown]);

  const handleResendOTP = async () => {
    try {
      setIsResending(true);
      const otpUuid = await requestOtp(verification?.number, 'register');
      setVerification((prev) => ({ ...prev, id: otpUuid, otpError: null }));
      setResendExpiry(moment().add(1, 'minute').toISOString());
    } catch (error) {
      console.error(error);
      setError('afterSubmit', { message: getRegisterErrorMessage(error, translate) });
    } finally {
      setIsResending(false);
    }
  };

  const onSubmit = async (data) => {
    setVerification((prev) => ({ ...prev, otpCode: data.otp.trim(), isVerified: true, otpError: null }));
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2} sx={{ justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center', px: 4 }}>
          <Typography variant="body1" fontWeight={300}>
            {translate('we_have_sent_otp')}
          </Typography>
          <Typography variant="subtitle2" fontWeight={700}>
            {verification?.number}
          </Typography>
        </Box>

        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        <RHFTextField
          name="otp"
          label="OTP"
          InputProps={{
            inputProps: { type: 'text', inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 },
          }}
        />

        {!resendExpiry ? (
          <Typography
            variant="body2"
            sx={{ pointerEvents: isResending ? 'none' : 'auto', opacity: isResending ? 0.6 : 1 }}
          >
            {translate('didnt_receive')}{' '}
            <Link color={'primary'} onClick={() => handleResendOTP()} sx={{ cursor: 'pointer' }}>
              {translate('send_again')}
            </Link>
          </Typography>
        ) : (
          <Typography variant="caption" sx={{ opacity: 0.6 }}>
            {translate('resend_again')} {countdown.seconds}
          </Typography>
        )}

        <GoldBorderLoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="outlined"
          loading={isSubmitting}
          sx={{ py: 0, height: '40px' }}
        >
          {translate('next')}
        </GoldBorderLoadingButton>
      </Stack>
    </FormProvider>
  );
}

RegisterOTPForm.propTypes = {
  verification: PropTypes.object,
  setVerification: PropTypes.func,
};
